import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertTriangle, CheckCircle2, ExternalLink, FileText, Loader2,
  RefreshCw, TrendingDown, TrendingUp, Minus,
} from "lucide-react";
import { EXTRACTION_LABELS } from "@/lib/localBloodTestOcr";
import { REPORT_KIND_LABELS } from "@/components/blood-tests/bloodTestLabels";

const STATUS_CONFIG = {
  normal: { label: "Normal", className: "bg-green-100 text-green-700", icon: CheckCircle2 },
  low: { label: "Bas", className: "bg-amber-100 text-amber-700", icon: TrendingDown },
  high: { label: "Élevé", className: "bg-orange-100 text-orange-700", icon: TrendingUp },
  critical_low: { label: "Critique bas", className: "bg-red-100 text-red-700", icon: AlertTriangle },
  critical_high: { label: "Critique élevé", className: "bg-red-100 text-red-700", icon: AlertTriangle },
  unknown: { label: "—", className: "bg-slate-100 text-slate-600", icon: Minus },
};

const CATEGORY_STATUS = {
  ok: "bg-green-100 text-green-700",
  attention: "bg-amber-100 text-amber-700",
  alert: "bg-red-100 text-red-700",
};

function MarkerBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.unknown;
  const Icon = cfg.icon;
  return (
    <Badge className={`${cfg.className} font-medium gap-1`}>
      <Icon className="w-3 h-3" />
      {cfg.label}
    </Badge>
  );
}

export default function BloodTestDetail({ report, loading, onReanalyze, canDelete, onDelete, reanalyzing }) {
  const markersByCategory = useMemo(() => {
    if (!report?.markers?.length) return {};
    return report.markers.reduce((acc, m) => {
      const cat = m.category || "Autre";
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(m);
      return acc;
    }, {});
  }, [report]);

  const outOfRange = useMemo(() => {
    if (!report?.markers) return 0;
    return report.markers.filter(m => m.status && m.status !== "normal" && m.status !== "unknown").length;
  }, [report]);

  if (loading) {
    return (
      <div className="space-y-4 mt-6">
        <Skeleton className="h-32 w-full rounded-2xl" />
        <Skeleton className="h-64 w-full rounded-2xl" />
      </div>
    );
  }

  if (!report) return null;

  const analysis = report.analysis;
  const isProcessing = report.ai_status === "processing" || report.ai_status === "pending";
  const isError = report.ai_status === "error";

  return (
    <div className="mt-6 space-y-6">
      {/* Header card */}
      <Card className="border-slate-100">
        <CardContent className="p-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="font-heading text-xl font-bold text-slate-900">{report.title || "Bilan sanguin"}</h2>
                {report.report_kind && report.report_kind !== "standalone" && (
                  <Badge className={(REPORT_KIND_LABELS[report.report_kind] || REPORT_KIND_LABELS.standalone).className}>
                    {(REPORT_KIND_LABELS[report.report_kind] || REPORT_KIND_LABELS.standalone).label}
                  </Badge>
                )}
              </div>
              <div className="flex flex-wrap gap-3 mt-2 text-sm text-slate-500">
                {report.track_name && <span className="text-indigo-600">↳ Suivi : {report.track_name}</span>}
                {report.lab_name && <span>🏥 {report.lab_name}</span>}
                {report.lab_date && <span>📅 {new Date(report.lab_date + "T12:00:00").toLocaleDateString("fr-FR")}</span>}
                {report.student_name && <span>👤 {report.student_name}</span>}
                {report.extraction_method && (
                  <span>📄 {EXTRACTION_LABELS[report.extraction_method] || report.extraction_method}</span>
                )}
                <span>🧪 {report.marker_count || report.markers?.length || 0} biomarqueurs</span>
                {outOfRange > 0 && (
                  <span className="text-amber-600 font-medium">{outOfRange} hors normes</span>
                )}
              </div>
              {report.notes && <p className="text-sm text-slate-600 mt-3">{report.notes}</p>}
            </div>
            <div className="flex flex-wrap gap-2">
              {report.file_url && (
                <Button variant="outline" size="sm" className="rounded-full" asChild>
                  <a href={report.file_url} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="w-4 h-4 mr-2" /> Voir le document
                  </a>
                </Button>
              )}
              {onReanalyze && (
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-full"
                  onClick={onReanalyze}
                  disabled={reanalyzing || isProcessing}
                >
                  {reanalyzing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                  Réanalyser
                </Button>
              )}
              {canDelete && onDelete && (
                <Button variant="outline" size="sm" className="rounded-full text-red-600 hover:text-red-700" onClick={onDelete}>
                  Supprimer
                </Button>
              )}
            </div>
          </div>

          {isProcessing && (
            <div className="mt-4 flex items-center gap-2 text-sm text-blue-600 bg-blue-50 rounded-xl px-4 py-3">
              <Loader2 className="w-4 h-4 animate-spin" />
              Analyse IA en cours… extraction et interprétation de tous les biomarqueurs.
            </div>
          )}
          {isError && (
            <div className="mt-4 flex items-start gap-2 text-sm text-red-600 bg-red-50 rounded-xl px-4 py-3">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <div>
                <p>{report.ai_error || "L'analyse IA a échoué. Réessayez avec le bouton « Réanalyser »."}</p>
                <p className="text-xs text-red-500 mt-1">Essayez un PDF texte, l'OCR navigateur, ou collez manuellement les résultats.</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* AI Analysis */}
      {analysis && (
        <>
          <Card className="border-purple-100 bg-gradient-to-br from-purple-50/50 to-white">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="w-5 h-5 text-purple-600" />
                Synthèse IA
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {analysis.overview && (
                <p className="text-slate-700 leading-relaxed">{analysis.overview}</p>
              )}

              {analysis.categories_review?.length > 0 && (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {analysis.categories_review.map((cat, i) => (
                    <div key={i} className="bg-white rounded-xl border border-slate-100 p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-slate-900">{cat.category}</span>
                        <Badge className={CATEGORY_STATUS[cat.status] || CATEGORY_STATUS.attention}>
                          {cat.status === "ok" ? "OK" : cat.status === "alert" ? "Alerte" : "Attention"}
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-600">{cat.comment}</p>
                      {cat.markers_count > 0 && (
                        <p className="text-xs text-slate-400 mt-1">{cat.markers_count} marqueurs</p>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {analysis.abnormal_findings?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-slate-900 mb-3">Valeurs hors normes</h3>
                  <div className="space-y-3">
                    {analysis.abnormal_findings.map((item, i) => (
                      <div key={i} className="bg-white rounded-xl border border-amber-100 p-4">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-slate-900">{item.marker}</span>
                          <span className="text-sm text-slate-500">{item.value}</span>
                          {item.severity && (
                            <Badge variant="outline" className="text-xs">
                              {item.severity === "severe" ? "Sévère" : item.severity === "moderate" ? "Modéré" : "Léger"}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-600">{item.interpretation}</p>
                        {item.coaching_impact && (
                          <p className="text-sm text-blue-600 mt-1">💪 {item.coaching_impact}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-4">
                {analysis.coaching_recommendations?.length > 0 && (
                  <RecommendationList title="Recommandations coaching" items={analysis.coaching_recommendations} color="blue" />
                )}
                {analysis.nutrition_recommendations?.length > 0 && (
                  <RecommendationList title="Recommandations nutrition" items={analysis.nutrition_recommendations} color="green" />
                )}
                {analysis.training_recommendations?.length > 0 && (
                  <RecommendationList title="Adaptations entraînement" items={analysis.training_recommendations} color="orange" />
                )}
                {analysis.follow_up_suggestions?.length > 0 && (
                  <RecommendationList title="Suivi suggéré" items={analysis.follow_up_suggestions} color="purple" />
                )}
              </div>

              {analysis.disclaimer && (
                <p className="text-xs text-slate-400 italic border-t border-slate-100 pt-4">{analysis.disclaimer}</p>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* All markers table */}
      {report.markers?.length > 0 && (
        <Card className="border-slate-100">
          <CardHeader>
            <CardTitle className="text-lg">
              Tous les biomarqueurs ({report.markers.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {Object.entries(markersByCategory).map(([category, markers]) => (
              <div key={category}>
                <h3 className="text-sm font-bold uppercase tracking-wider text-slate-400 mb-3">{category}</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-100 text-left text-slate-500">
                        <th className="pb-2 pr-4 font-medium">Marqueur</th>
                        <th className="pb-2 pr-4 font-medium">Valeur</th>
                        <th className="pb-2 pr-4 font-medium hidden sm:table-cell">Référence</th>
                        <th className="pb-2 font-medium">Statut</th>
                      </tr>
                    </thead>
                    <tbody>
                      {markers.map((m, i) => (
                        <tr key={i} className="border-b border-slate-50 hover:bg-slate-50/50">
                          <td className="py-2.5 pr-4 font-medium text-slate-900">{m.name}</td>
                          <td className="py-2.5 pr-4 text-slate-700">
                            {m.value}{m.unit ? ` ${m.unit}` : ""}
                          </td>
                          <td className="py-2.5 pr-4 text-slate-500 hidden sm:table-cell">
                            {m.reference_text
                              || (m.reference_min != null && m.reference_max != null
                                ? `${m.reference_min} – ${m.reference_max}`
                                : "—")}
                          </td>
                          <td className="py-2.5"><MarkerBadge status={m.status} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function RecommendationList({ title, items, color }) {
  const dotColor = {
    blue: "bg-blue-500",
    green: "bg-green-500",
    orange: "bg-orange-500",
    purple: "bg-purple-500",
  }[color] || "bg-slate-500";

  return (
    <div className="bg-white rounded-xl border border-slate-100 p-4">
      <h3 className="font-semibold text-slate-900 mb-3">{title}</h3>
      <ul className="space-y-2">
        {items.map((item, i) => (
          <li key={i} className="flex gap-2 text-sm text-slate-600">
            <span className={`w-1.5 h-1.5 rounded-full ${dotColor} mt-2 shrink-0`} />
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}